import React, { useCallback, useEffect, useMemo, useState } from "react";
import qs from "qs";
import { Card } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { useSelector } from "react-redux";
import {
  deleteResource,
  getResourceAPI,
  postResource,
  updateResource,
} from "../../../redux/slices/resourceSlice.js";
import { BodyWithPadding } from "../../../styles/genericStyles.js";
import TabCreateModal from "../../search/TabCreateModal.jsx";
import { isEmpty, startCase } from "lodash";
import { tabNameToPath } from "../../../utils/strings.js";
import {
  getUIData,
  getUIProperty,
  setUIData,
  setUIProperty,
} from "../../../redux/slices/uiSlice.js";
import { useDispatch } from "react-redux";
import notify from "devextreme/ui/notify";
import Loader from "../../generic/Loader.jsx";
import WarningModal from "../../generic/WarningModal.jsx";
import useResourceStatuses from "../../../hooks/useResourceStatus.jsx";
import useResources from "../../../hooks/useResources";
import TabPanelActions from "./TabPanelActions.jsx";
import TabPanel from "./TabPanel.jsx";
import { ContextMenu } from "devextreme-react";
import { API_URL, contextMenuConfig } from "../../search/config.js";
import ReportInfoModal from "../../generic/ReportInfoModal.jsx";
import PaymentsModal from "../../generic/PaymentsModal.jsx";
import { usePrevious } from "../../../hooks/usePrevious.jsx";
import { openAuthorizedWindow } from "../../../utils/api.js";

const formatData = (data, extra) => ({
  ...data,
  ...extra,
  trade_case_id: data.selectedTradeCaseId,
  date: data.date,
});
// TODO: see what is happening with the index of pagination
const TabsPagination = ({ tabName = "", cells = [], itemInfoFields = {} }) => {
  const dispatch = useDispatch();

  const page = useSelector((state) => getUIProperty(state, "page")) || 1;

  const [showWarning, setShowWarning] = useState(false);
  const [showReportInfoModal, setShowReportInfoModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [ready, setReady] = useState(true);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const { tradeCases, lineItems, [tabName]: tabData } = useResources();
  const prevLineItems = usePrevious(lineItems);
  const { vesselSystemStatus, searchDetailsStatus } = useResourceStatuses();
  const itemInfo = useSelector((state) => getUIData(state, "itemInfo"));

  const selectedTradeCaseId = useSelector((state) =>
    getUIProperty(state, "selectedTradeCaseId")
  );
  const selectedLineItems = useSelector((state) =>
    getUIData(state, "selectedLineItems")
  );

  // const tabData = useSelector((state) => getResourceState(state, tabName));

  const filteredTabData = useMemo(
    () => Object.values(tabData || {}).find((data, idx) => idx === page - 1),
    [tabData, page]
  );

  const stageId = useMemo(
    () => (filteredTabData && filteredTabData.id) || "new",
    [filteredTabData]
  );

  const reduxName = useMemo(
    () => `${tabNameToPath[tabName]}:${stageId}`,
    [stageId, tabName]
  );

  const data = useSelector((state) => getUIData(state, reduxName));

  console.log({data});
  
  const vessel_id = useMemo(() => {
    if (!(tradeCases instanceof Array)) return;

    const foundTradeCase = tradeCases.find(
      (tc) => tc.id === selectedTradeCaseId
    );

    if (!foundTradeCase) return;
    return foundTradeCase.vessel_id;
  }, [selectedTradeCaseId, tradeCases]);

  useEffect(() => {
    dispatch(
      setUIProperty({
        name: "tabName",
        value: tabNameToPath[tabName],
      })
    );
  }, [dispatch, tabName]);

  useEffect(() => {
    if (lineItems instanceof Array) {
      dispatch(
        setUIProperty({
          name: "filteredItems",
          value:
            lineItems instanceof Array
              ? lineItems.filter((listItem) => listItem.stage_id === stageId)
              : [],
        })
      );
    }
  }, [filteredTabData, lineItems, dispatch, stageId, prevLineItems]);

  useEffect(() => {
    if (!vessel_id) return;

    dispatch(
      getResourceAPI({
        path: "vessel-system",
        query: {
          vessel_id,
        },
      })
    );

    dispatch(
      setUIData({
        name: reduxName,
        value: { vessel_id, selectedTradeCaseId },
      })
    );

    if (!isEmpty(filteredTabData)) {
      dispatch(
        setUIData({
          name: reduxName,
          value: { vessel_id, selectedTradeCaseId },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessel_id, stageId]);

  const viewReport = useCallback(
    async (reportInfo) => {
      if (stageId) {
        const { fileName, pdfUrl } = await openAuthorizedWindow(
          `${API_URL}/resource/${
            tabNameToPath[tabName]
          }/${stageId}/report/?${qs.stringify(reportInfo)}`,
          setReady
        );

        dispatch(setUIProperty({ name: "pdfUrl", value: pdfUrl }));
        dispatch(setUIProperty({ name: "fileName", value: fileName }));
      }
    },
    [dispatch, stageId, tabName]
  );

  const viewSpecificReport = useCallback(
    async (reportType, param) => {
      if (stageId) {
        const url = param
          ? `${API_URL}/resource/order-acknowledgment/${stageId}/${reportType}/?details=${param}`
          : `${API_URL}/resource/order-acknowledgment/${stageId}/${reportType}`;
        const { fileName, pdfUrl } = await openAuthorizedWindow(url, setReady);

        dispatch(setUIProperty({ name: "pdfUrl", value: pdfUrl }));
        dispatch(setUIProperty({ name: "fileName", value: fileName }));
      }
    },
    [dispatch, stageId]
  );

  const handleChange = useCallback(
    (event, value) => dispatch(setUIProperty({ name: "page", value })),
    [dispatch]
  );

  const handleContextMenu = useCallback(
    ({ itemData }) => {
      const { path, resource, mode, per_vendor, text } = itemData;

      let resourceData = {};

      if (mode === "single" && stageId) {
        resourceData = [
          {
            id: stageId,
            line_items:
              selectedLineItems[stageId] ||
              Object.values(lineItems || {})
                .filter((listItem) => listItem.stage_id === stageId)
                .map((filteredItem) => filteredItem.id),
          },
        ];
      } else if (mode === "multiple") {
        resourceData = tabData.map((data) => ({
          id: data.id,
          line_items:
            selectedLineItems[data.id] ||
            Object.values(lineItems || {})
              .filter((listItem) => listItem.stage_id === data.id)
              .map((filteredItem) => filteredItem.id),
        }));
      } else if (mode === "report") {
        viewReport();
      } else if (mode === "reportWithAddresses") {
        setShowReportInfoModal(true);
      } else if (mode === "specificReport") {
        viewSpecificReport(path, text);
      } else if (mode === "payments") {
        setShowPaymentsModal(true);
      }

      const data = {
        ...(per_vendor ? { per_vendor: per_vendor } : {}),
        trade_case_id: selectedTradeCaseId,
        [resource]: resourceData,
      };

      if (!isEmpty(resourceData))
        dispatch(
          postResource({
            path: path,
            data,
          })
        ).then(({ payload }) => {
          const { path } = payload;
          notify(`${startCase(path)} created`, "info", 3000);
          dispatch(
            getResourceAPI({
              path: "search-details",
              query: { trade_case_id: selectedTradeCaseId },
            })
          );
        });
    },
    [
      stageId,
      selectedTradeCaseId,
      dispatch,
      selectedLineItems,
      lineItems,
      tabData,
      viewReport,
      viewSpecificReport,
    ]
  );

  const handleSave = useCallback(
    () =>{
    

      dispatch(    
        updateResource({
          id: stageId,
          path: tabNameToPath[tabName],
          data: formatData(data,itemInfo),
        })
      ).then(({ payload }) => {
        if (payload.error) {
          notify(payload.error, "error", 1000);
          return;
        }

        notify(`Successfully edited ${tabNameToPath[tabName]}`, "info", 1000);

        return;
      })},
    [dispatch, stageId, tabName, data, itemInfo]
  );

  const handleDelete = useCallback(() => {
    setReady(false);

    return dispatch(
      deleteResource({
        id: stageId,
        path: tabNameToPath[tabName],
      })
    ).then(() => {
      setReady(true);
      setShowWarning(false);
      notify(`Successfully deleted ${tabNameToPath[tabName]}`, "info", 1000);
      dispatch(setUIProperty({ name: "page", value: 1 }));
      return;
    });
  }, [dispatch, stageId, tabName]);

  return (
    <>
      <div style={{ margin: "8px 0px 8px 0px" }}>
        <TabPanelActions
          name={tabNameToPath[tabName]}
          id={stageId}
          setOpenCreateModal={setOpenCreateModal}
          setShowWarning={setShowWarning}
          handleSave={handleSave}
        />
        {filteredTabData && showWarning && (
          <WarningModal
            ready={ready}
            id={stageId}
            text={`Are you sure you want to delete ${tabNameToPath[tabName]}`}
            visible={showWarning}
            handleYes={handleDelete}
            handleNo={() => setShowWarning(false)}
          />
        )}
        {showReportInfoModal && (
          <ReportInfoModal
            viewReport={viewReport}
            visible={showReportInfoModal}
            handleCancel={() => setShowReportInfoModal(false)}
          />
        )}

        {showPaymentsModal && (
          <PaymentsModal
            id={stageId}
            trade_case_id={filteredTabData.trade_case_id}
            visible={showPaymentsModal}
            handleCancel={() => setShowPaymentsModal(false)}
          />
        )}
      </div>
      <Card
        id="card"
        style={{
          backgroundColor: "lightgray",
          position: "relative",
          minHeight: "200px",
        }}
      >
        <div>
          {stageId && (
            <ContextMenu
              dataSource={contextMenuConfig[tabNameToPath[tabName]]}
              width={200}
              target="#card"
              onItemClick={handleContextMenu}
            />
          )}
          {(searchDetailsStatus === "pending" ||
            vesselSystemStatus === "pending" ||
            !ready) && <Loader />}
          {isEmpty(filteredTabData) ? (
            <p style={{ textAlign: "center" }}>No {tabName} yet...</p>
          ) : (
            <BodyWithPadding padding="2%">
              <TabPanel
                itemInfoFields={itemInfoFields}
                cells={cells}
                filteredTabData={filteredTabData}
                tabName={tabName}
                selectedTradeCaseId={selectedTradeCaseId}
                data={data}
              />
              <Pagination
                page={page}
                style={{ paddingTop: "16px" }}
                count={Object.values(tabData || {}).length}
                color="primary"
                onChange={handleChange}
              />
            </BodyWithPadding>
          )}
        </div>
      </Card>
      {openCreateModal && (
        <TabCreateModal
          formatData={formatData}
          tabData={filteredTabData}
          vessel_id={vessel_id}
          tabName={tabName}
          data={data}
          reduxName={`${tabNameToPath[tabName]}:new`}
          cells={cells}
          visible={openCreateModal}
          setVisible={setOpenCreateModal}
        />
      )}
    </>
  );
};
export default TabsPagination;
